// @greenlight-spec: 12-azure-installation.md
// @greenlight-spec: 27-fleet-health-monitor.md
// @greenlight-spec: 28-it-dashboard-ui.md

import * as React from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  ContactDisclosure,
  ContactEditor,
  useContactEditor,
} from '@/components/greenlight/org/ContactEditor';
import { StepHeader } from '@/components/install/StepHeader';
import { api } from '@/lib/api';
import { errorBodyCode, thrownCode } from '@/lib/apiErrors';
import type { InstallContact, InstallationState } from '@greenlight/shared';

interface ContactStepProps {
  installationState: InstallationState;
  /** The contact already recorded, so a resumed wizard shows it instead of re-asking. */
  contact: InstallContact | null;
  onSaved: () => Promise<void>;
  isSubmitting: boolean;
}

/**
 * The Organization step asked for the installer's email, and the IdP step may
 * have named an initial administrator; either is the closest thing the wizard
 * knows to the person at the keyboard. Best-effort: the wizard is
 * unauthenticated, so there is no verified identity to read, and the field is
 * editable because the right commercial contact is often a shared billing
 * address rather than whoever is installing.
 */
function prefilledEmail(installationState: InstallationState): string {
  const installer = installationState.steps?.org_setup?.details?.['installer_email'];
  if (typeof installer === 'string') return installer;
  const admin = installationState.steps?.idp?.details?.['initial_admin_email'];
  return typeof admin === 'string' ? admin : '';
}

export function ContactStep({
  installationState,
  contact,
  onSaved,
  isSubmitting,
}: ContactStepProps): React.JSX.Element {
  const editor = useContactEditor(
    contact?.contact_name ?? '',
    contact?.contact_email ?? prefilledEmail(installationState),
  );
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const saveMutation = api.install.saveContact.useMutation();

  const stepComplete = installationState.steps?.contact?.status === 'complete';

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSaveError(null);

    const parsed = editor.validate();
    if (!parsed) return;

    try {
      const result = await saveMutation.mutateAsync({ body: parsed });
      if (result.status !== 200) {
        setSaveError(messageFor(errorBodyCode(result.body)));
        return;
      }
    } catch (err) {
      setSaveError(messageFor(thrownCode(err)));
      return;
    }
    await onSaved();
  };

  const busy = isSubmitting || saveMutation.isPending;
  const error = editor.error ?? saveError;

  return (
    // `noValidate`: the email input's type is here for the mobile keyboard, not
    // for the browser's own bubble — this step reports both fields in one alert.
    <form className="space-y-6" noValidate onSubmit={handleSubmit} data-testid="step-contact">
      <StepHeader step="contact" title="Who should Shift contact?">
        Greenlight is a paid product. Name the administrator Shift reaches about this install's
        trial and subscription.
      </StepHeader>

      <ContactEditor editor={editor} disabled={busy} idPrefix="contact" />

      <ContactDisclosure />

      {error && (
        <Alert variant="destructive" data-testid="contact-error">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Button
        type="submit"
        className="w-full md:w-auto h-11 md:h-9"
        disabled={busy}
        data-testid="step-next"
      >
        {busy ? 'Saving…' : stepComplete ? 'Save and continue' : 'Continue'}
      </Button>
    </form>
  );
}

function messageFor(code: string | undefined): string {
  if (code === 'install.already_complete') {
    return 'This install has already finished. Change it in Settings → Organization instead.';
  }
  return 'Could not save the contact. Check the control plane is running and try again.';
}
